import { Icon } from '@iconify/react';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import type { PadelGameListItem } from '@/services/padelService';
import { type SetScoreInput, PADEL_MAX_SCORE, isSet3Allowed } from './padelScoringUtils';
import { playersLabel } from './padelUiUtils';

interface PadelSetScorePanelProps {
  game: PadelGameListItem;
  sets: SetScoreInput[];
  canEdit: boolean;
  onSetsChange: (sets: SetScoreInput[]) => void;
}

export default function PadelSetScorePanel({
  game,
  sets,
  canEdit,
  onSetsChange,
}: PadelSetScorePanelProps) {
  const set3Allowed = isSet3Allowed(sets);

  const updateSet = (
    setNumber: number,
    patch: Partial<Pick<SetScoreInput, 'scoreSideA' | 'scoreSideB'>>
  ) => {
    if (!canEdit) return;
    if (setNumber === 3 && !set3Allowed) return;

    const next = sets.map((s) => {
      if (s.setNumber !== setNumber) return s;
      const scoreSideA = Math.min(PADEL_MAX_SCORE, Math.max(0, patch.scoreSideA ?? s.scoreSideA));
      const scoreSideB = Math.min(PADEL_MAX_SCORE, Math.max(0, patch.scoreSideB ?? s.scoreSideB));
      return { ...s, scoreSideA, scoreSideB };
    });
    onSetsChange(next);
  };

  const bump = (setNumber: number, side: 'a' | 'b', delta: number) => {
    const row = sets.find((s) => s.setNumber === setNumber);
    if (!row) return;
    const val = (side === 'a' ? row.scoreSideA : row.scoreSideB) + delta;
    updateSet(setNumber, side === 'a' ? { scoreSideA: val } : { scoreSideB: val });
  };

  const sideALabel = playersLabel(game.sideAPlayers) || 'Tim A';
  const sideBLabel = playersLabel(game.sideBPlayers) || 'Tim B';

  return (
    <div className="space-y-4">
      {game.maxSets !== 1 && !set3Allowed && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Set 3 aktif setelah masing-masing tim menang 1 set.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {sets.map((set) => {
          const disabled = set.setNumber === 3 && !set3Allowed;
          return (
            <div
              key={set.setNumber}
              className={`rounded-xl border p-4 ${disabled ? 'opacity-50 bg-gray-50' : 'bg-white'}`}
            >
              <p className="text-center font-semibold text-gray-700 mb-3">Set {set.setNumber}</p>
              <div className="space-y-3">
                <ScoreRow
                  label={sideALabel}
                  value={set.scoreSideA}
                  disabled={disabled || !canEdit}
                  onMinus={() => bump(set.setNumber, 'a', -1)}
                  onPlus={() => bump(set.setNumber, 'a', 1)}
                  onInput={(v) => {
                    const n = Number.parseInt(v, 10);
                    if (!Number.isNaN(n)) updateSet(set.setNumber, { scoreSideA: n });
                  }}
                />
                <ScoreRow
                  label={sideBLabel}
                  value={set.scoreSideB}
                  disabled={disabled || !canEdit}
                  onMinus={() => bump(set.setNumber, 'b', -1)}
                  onPlus={() => bump(set.setNumber, 'b', 1)}
                  onInput={(v) => {
                    const n = Number.parseInt(v, 10);
                    if (!Number.isNaN(n)) updateSet(set.setNumber, { scoreSideB: n });
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScoreRow({
  label,
  value,
  disabled,
  onMinus,
  onPlus,
  onInput,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onMinus: () => void;
  onPlus: () => void;
  onInput: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-xs text-gray-500 truncate mb-1">{label}</p>
      <div className="flex items-center justify-center gap-2">
        <ShadcnButton
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          onClick={onMinus}
        >
          <Icon icon="mdi:minus" className="w-4 h-4" />
        </ShadcnButton>
        <Input
          className="w-16 text-center text-xl font-bold h-12"
          value={String(value)}
          disabled={disabled}
          onChange={(e) => onInput(e.target.value)}
        />
        <ShadcnButton
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          onClick={onPlus}
        >
          <Icon icon="mdi:plus" className="w-4 h-4" />
        </ShadcnButton>
      </div>
    </div>
  );
}
